import * as THREE from 'three'
import {
  GaussianSplatMesh,
  type GaussianSplatMeshOptions,
} from '@/plugins/gsplat/core/GaussianSplatMesh'
import {
  GSplatSogsData,
  registerGSplatSogsTextureRestoreSource,
} from '@/plugins/gsplat/core/GSplatSogsData'
import type { SogsData } from '@/plugins/gsplat/core/SogsData'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import {
  createEffectiveSogsMeta,
  isSogsMetaV1,
  isSogsMetaV2,
  type SogsMetaV2,
  SogsParser,
} from '@/plugins/gsplat/loaders/SogsParser'
import {
  beginGSplatResourceAttempt,
  measureGSplatLoadStage,
} from '@/plugins/gsplat/telemetry/GSplatLoadTelemetry'
import { syncTextureUploadVersion } from '@/plugins/gsplat/utils/syncTextureUploadVersion'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatLoadTelemetryObserver } from '@/shared/types/gsplatTelemetry'
import { SOGLoader, type SOGLoaderOptions } from './SOGLoader'

/** SOG 加载 logger（命名空间 gsplat:loader），perf 埋点经 log.perf 输出 */
const log = getLoggerManager().getLogger('gsplat:loader')

/**
 * ZIP 文件结构
 */
interface ZipFile {
  filename: string
  compression: 'none' | 'deflate' | 'unknown'
  data: Uint8Array
}

export interface GpuDataLoadOptions {
  renderer?: THREE.WebGLRenderer
  preuploadTextures?: boolean
  retainTextureRestoreSource?: boolean
  signal?: AbortSignal
  /** 是否加载高阶球谐数据 SHN，默认 true；SH0 基础颜色始终加载 */
  loadSHN?: boolean
  /** 单次模型加载的只读遥测 observer */
  telemetry?: GSplatLoadTelemetryObserver
  /** 同一资源从 1 开始递增的底层 attempt 序号 */
  telemetryAttempt?: number
}

function createAbortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw createAbortError()
  }
}

/**
 * SOG Bundle (.sog) 加载器
 *
 * 支持加载打包为 ZIP 格式的 SOG 资源
 * ZIP 内包含：
 * - meta.json: 元数据
 * - *.webp: 压缩纹理数据
 *
 * 格式特点：
 * - 单文件分发，便于传输
 * - 支持 deflate 压缩
 * - 使用浏览器原生 DecompressionStream API
 */
export class SOGBundleLoader {
  private sogLoader: SOGLoader
  private options: SOGLoaderOptions

  constructor(options: SOGLoaderOptions = {}) {
    this.options = options
    this.sogLoader = new SOGLoader(options)
  }

  /**
   * 从 .sog Bundle 文件加载 Splat 数据
   * @param url .sog 文件的 URL
   * @returns SplatData 或 SogsData（取决于 keepCompressed 选项）
   */
  async load(url: string): Promise<SplatData | SogsData> {
    // 1. 下载 .sog 文件
    const arrayBuffer = await this.downloadArrayBuffer(
      url,
      this.options.signal,
      this.options.telemetry
    )

    // 2. 从 ArrayBuffer 解析
    return measureGSplatLoadStage(
      this.options.telemetry,
      {
        resourceType: 'sog-bundle',
        resourceIdentity: url,
        resourceUrl: url,
        stage: 'bundle-parse',
      },
      () => this.parseBuffer(arrayBuffer)
    )
  }

  /**
   * 从 ArrayBuffer 解析 .sog Bundle 数据
   * @param arrayBuffer .sog 文件的 ArrayBuffer
   * @returns SplatData 或 SogsData（取决于 keepCompressed 选项）
   */
  async parseBuffer(arrayBuffer: ArrayBuffer): Promise<SplatData | SogsData> {
    const { signal } = this.options
    throwIfAborted(signal)

    // 1. 解析 ZIP 结构
    const files = this.parseZipArchive(arrayBuffer, signal)

    // 2. 解压 deflate 压缩的文件
    for (const file of files) {
      throwIfAborted(signal)
      if (file.compression === 'deflate') {
        file.data = await this.inflate(file.data, signal)
      }
    }

    // 3. 提取 meta.json
    const metaFile = files.find((f) => f.filename === 'meta.json')
    if (!metaFile) {
      throw new Error('SOG Bundle 中未找到 meta.json')
    }

    // 4. 解析元数据
    const metaText = new TextDecoder().decode(metaFile.data)
    const meta = JSON.parse(metaText)

    // 5. 从内存中的文件加载 SOGS 数据
    return this.loadFromMemory(meta, files)
  }

  /**
   * 以 GPU 纹理路径加载 .sog Bundle，避免把全部 WebP 解码成 CPU RGBA 数组。
   */
  async loadGpuData(url: string, options: GpuDataLoadOptions = {}): Promise<GSplatSogsData> {
    const signal = options.signal ?? this.options.signal
    const telemetry = options.telemetry ?? this.options.telemetry
    const telemetryAttempt = options.telemetryAttempt ?? 1
    throwIfAborted(signal)

    const arrayBuffer = await this.downloadArrayBuffer(url, signal, telemetry, telemetryAttempt)
    const { files, meta } = await this.parseBundle(
      arrayBuffer,
      signal,
      telemetry,
      url,
      telemetryAttempt
    )
    const metaV2 = createEffectiveSogsMeta(this.normalizeMeta(meta), options.loadSHN ?? true)
    this.patchMetaCodebooks(metaV2)
    const requiredFilenames = this.getTextureFilenames(metaV2)
    const fileMap = this.createFileMap(files, requiredFilenames)

    const textureController = new AbortController()
    const abortTextures = () => textureController.abort()
    if (signal) {
      if (signal.aborted) {
        abortTextures()
      } else {
        signal.addEventListener('abort', abortTextures, { once: true })
      }
    }

    const requireTexture = async (filename: string, name: string): Promise<THREE.Texture> => {
      const file = fileMap.get(filename)
      if (!file) {
        throw new Error(`SOG Bundle 中缺少文件: ${filename}`)
      }

      const resourceIdentity = `${url.split(/[?#]/)[0]}|${filename}`
      const data = await measureGSplatLoadStage(
        telemetry,
        {
          resourceType: 'sog-texture',
          resourceIdentity,
          resourceUrl: url,
          attempt: telemetryAttempt,
          stage: 'bundle-inflate',
        },
        () => this.extractFile(file, textureController.signal)
      )

      return this.loadWebPTextureFromMemory(
        data,
        name,
        {
          ...options,
          telemetry,
          telemetryAttempt,
          signal: textureController.signal,
        },
        resourceIdentity,
        url
      )
    }

    const completedTextures: THREE.Texture[] = []
    const loadTexture = async (filename: string, name: string): Promise<THREE.Texture> => {
      const texture = await requireTexture(filename, name)
      completedTextures.push(texture)
      return texture
    }
    try {
      // 文件级并发仍由 GSplatAssetLoader 控制；单个 bundle 内逐张解码、上传并立即
      // 释放背板，避免 8 个文件各自同时驻留 5～7 张 ImageBitmap。
      const meansLow = await loadTexture(metaV2.means.files[0], 'sogsMeansLow')
      const meansHigh = await loadTexture(metaV2.means.files[1], 'sogsMeansHigh')
      const quats = await loadTexture(metaV2.quats.files[0], 'sogsQuats')
      const scales = await loadTexture(metaV2.scales.files[0], 'sogsScales')
      const sh0 = await loadTexture(metaV2.sh0.files[0], 'sogsSH0')
      const shCentroids = metaV2.shN?.files[0]
        ? await loadTexture(metaV2.shN.files[0], 'sogsSHCentroids')
        : undefined
      const shLabels = metaV2.shN?.files[1]
        ? await loadTexture(metaV2.shN.files[1], 'sogsSHLabels')
        : undefined

      return new GSplatSogsData({
        meta: metaV2,
        numSplats: metaV2.count,
        means_l: meansLow,
        means_u: meansHigh,
        quats,
        scales,
        sh0,
        sh_centroids: shCentroids,
        sh_labels: shLabels,
        url,
      })
    } catch (error) {
      textureController.abort()
      this.disposeTextures(completedTextures)
      if (signal?.aborted) {
        throw createAbortError()
      }
      throw error
    } finally {
      signal?.removeEventListener('abort', abortTextures)
    }
  }

  /**
   * 加载 SOG bundle 并生成 GaussianSplatMesh。
   *
   * @param url - bundle 资源 URL（通常以 .sog 结尾）
   * @param meshOptions - 可选 Mesh 构建参数
   * @returns 已绑定数据的 GaussianSplatMesh
   */
  async loadMesh(url: string, meshOptions?: GaussianSplatMeshOptions): Promise<GaussianSplatMesh> {
    const splatData = await this.load(url)
    return new GaussianSplatMesh(splatData, meshOptions)
  }

  /**
   * 下载 ArrayBuffer
   */
  private async downloadArrayBuffer(
    url: string,
    signal = this.options.signal,
    telemetry = this.options.telemetry,
    telemetryAttempt = 1
  ): Promise<ArrayBuffer> {
    return log.perf.span('download', async () => {
      const attempt = beginGSplatResourceAttempt(telemetry, {
        resourceType: 'sog-bundle',
        resourceIdentity: url,
        resourceUrl: url,
        attempt: telemetryAttempt,
      })
      try {
        throwIfAborted(signal)
        const init: RequestInit = {}
        if (this.options.withCredentials) {
          init.credentials = 'include'
        }
        if (signal) {
          init.signal = signal
        }
        const response = await fetch(url, init)
        attempt.markResponse(response)
        if (!response.ok) {
          attempt.fail(new Error('SOG bundle request failed'), `http-${response.status}`)
          throw new Error(`加载失败: ${response.status} ${response.statusText}`)
        }
        const result = await response.arrayBuffer()
        throwIfAborted(signal)
        attempt.complete(result.byteLength)
        return result
      } catch (error) {
        attempt.fail(error)
        throw error
      }
    })
  }

  private async parseBundle(
    arrayBuffer: ArrayBuffer,
    signal = this.options.signal,
    telemetry = this.options.telemetry,
    url = 'sog-bundle',
    telemetryAttempt = 1
  ): Promise<{ files: ZipFile[]; meta: Record<string, unknown> }> {
    return log.perf.span('decode', () =>
      measureGSplatLoadStage(
        telemetry,
        {
          resourceType: 'sog-bundle',
          resourceIdentity: url,
          resourceUrl: url,
          attempt: telemetryAttempt,
          stage: 'bundle-parse',
        },
        async () => {
          throwIfAborted(signal)
          const files = this.parseZipArchive(arrayBuffer, signal)

          const metaFile = files.find((file) => file.filename === 'meta.json')
          if (!metaFile) {
            throw new Error('SOG Bundle 中未找到 meta.json')
          }

          const metaText = new TextDecoder().decode(await this.extractFile(metaFile, signal))
          const meta = JSON.parse(metaText) as Record<string, unknown>

          return { files, meta }
        }
      )
    )
  }

  private normalizeMeta(meta: Record<string, unknown>): SogsMetaV2 {
    if (isSogsMetaV1(meta)) {
      return SogsParser.upgradeMeta(meta)
    }

    if (isSogsMetaV2(meta)) {
      return meta
    }

    throw new Error('Invalid SOG meta structure')
  }

  private patchMetaCodebooks(meta: SogsMetaV2): void {
    const codebookNames = ['scales', 'sh0', 'shN'] as const
    for (const name of codebookNames) {
      const codebook = meta[name]?.codebook as Array<number | null | undefined> | undefined
      if (codebook?.[0] === null) {
        codebook[0] = (codebook[1] ?? 0) + ((codebook[1] ?? 0) - (codebook[255] ?? 0)) / 255
      }
    }
  }

  private getTextureFilenames(meta: SogsMetaV2): Set<string> {
    return new Set([
      ...meta.means.files,
      ...meta.quats.files,
      ...meta.scales.files,
      ...meta.sh0.files,
      ...(meta.shN?.files ?? []),
    ])
  }

  private createFileMap(files: ZipFile[], filenames: Set<string>): Map<string, ZipFile> {
    const fileMap = new Map<string, ZipFile>()
    for (const file of files) {
      if (filenames.has(file.filename)) {
        fileMap.set(file.filename, file)
      }
    }
    return fileMap
  }

  private async extractFile(file: ZipFile, signal?: AbortSignal): Promise<Uint8Array> {
    throwIfAborted(signal)
    if (file.compression === 'deflate') {
      return this.inflate(file.data, signal)
    }
    if (file.compression === 'none') {
      return file.data
    }
    throw new Error(`SOG Bundle 使用了不支持的压缩方式: ${file.filename}`)
  }

  private async loadWebPTextureFromMemory(
    data: Uint8Array,
    name: string,
    options: GpuDataLoadOptions = {},
    resourceIdentity = name,
    resourceUrl = name
  ): Promise<THREE.Texture> {
    const { signal } = options
    throwIfAborted(signal)

    const buffer = data.buffer.slice(
      data.byteOffset,
      data.byteOffset + data.byteLength
    ) as ArrayBuffer
    const blob = new Blob([buffer], { type: 'image/webp' })
    let image: TexImageSource | undefined
    let texture: THREE.Texture | undefined

    try {
      image = await measureGSplatLoadStage(
        options.telemetry,
        {
          resourceType: 'sog-texture',
          resourceIdentity,
          resourceUrl,
          attempt: options.telemetryAttempt,
          stage: 'texture-decode',
        },
        async () => {
          if (typeof createImageBitmap !== 'undefined') {
            try {
              return await createImageBitmap(blob, {
                colorSpaceConversion: 'none',
                premultiplyAlpha: 'none',
              })
            } catch {
              throwIfAborted(signal)
              return this.loadImageFromBlob(blob, signal)
            }
          }
          return this.loadImageFromBlob(blob, signal)
        }
      )
      throwIfAborted(signal)

      texture = await measureGSplatLoadStage(
        options.telemetry,
        {
          resourceType: 'sog-texture',
          resourceIdentity,
          resourceUrl,
          attempt: options.telemetryAttempt,
          stage: 'gpu-prepare',
        },
        async () => {
          const nextTexture = new THREE.Texture(image)
          nextTexture.name = name
          nextTexture.minFilter = THREE.NearestFilter
          nextTexture.magFilter = THREE.NearestFilter
          nextTexture.wrapS = THREE.ClampToEdgeWrapping
          nextTexture.wrapT = THREE.ClampToEdgeWrapping
          nextTexture.generateMipmaps = false
          nextTexture.colorSpace = THREE.NoColorSpace
          nextTexture.flipY = false
          nextTexture.needsUpdate = true

          if (options.preuploadTextures !== false && options.renderer) {
            options.renderer.initTexture(nextTexture)
            syncTextureUploadVersion(options.renderer, nextTexture)
          }
          if (options.retainTextureRestoreSource) {
            const imageSize = image as { width: number; height: number }
            registerGSplatSogsTextureRestoreSource(
              nextTexture,
              blob,
              imageSize.width,
              imageSize.height
            )
          }
          return nextTexture
        }
      )
      throwIfAborted(signal)

      if (
        options.retainTextureRestoreSource &&
        options.preuploadTextures !== false &&
        options.renderer
      ) {
        this.releaseDecodedTextureImage(texture)
      }

      return texture
    } catch (error) {
      if (texture) {
        this.disposeTexture(texture)
      } else {
        this.closeImage(image)
      }
      if (signal?.aborted) {
        throw createAbortError()
      }
      throw error
    }
  }

  private loadImageFromBlob(blob: Blob, signal?: AbortSignal): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(createAbortError())
        return
      }

      const image = new Image()
      const objectUrl = URL.createObjectURL(blob)
      let settled = false

      const cleanup = () => {
        image.onload = null
        image.onerror = null
        signal?.removeEventListener('abort', onAbort)
        URL.revokeObjectURL(objectUrl)
      }
      const onAbort = () => {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        image.src = ''
        reject(createAbortError())
      }

      image.crossOrigin = 'anonymous'
      image.decoding = 'async'
      image.onload = () => {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        resolve(image)
      }
      image.onerror = () => {
        if (settled) {
          return
        }
        settled = true
        cleanup()
        image.src = ''
        reject(new Error('Failed to decode SOG WebP texture'))
      }
      signal?.addEventListener('abort', onAbort, { once: true })
      image.src = objectUrl
    })
  }

  private closeImage(image: unknown): void {
    if (!image || typeof image !== 'object') {
      return
    }

    if ('close' in image) {
      const close = image.close
      if (typeof close === 'function') {
        try {
          close.call(image)
        } catch {
          // ImageBitmap.close() is best-effort during teardown.
        }
      }
      return
    }

    if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement) {
      image.onload = null
      image.onerror = null
      image.src = ''
    }
  }

  private releaseDecodedTextureImage(texture: THREE.Texture): void {
    const image = texture.image as { width?: number; height?: number } | null | undefined
    const width = Number(image?.width)
    const height = Number(image?.height)
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) {
      return
    }

    try {
      this.closeImage(image)
    } finally {
      texture.image = { width, height }
    }
  }

  private disposeTexture(texture: THREE.Texture): void {
    const image = texture.image as unknown
    try {
      texture.dispose()
    } finally {
      try {
        this.closeImage(image)
      } finally {
        texture.image = null
      }
    }
  }

  private disposeTextures(textures: THREE.Texture[]): void {
    for (const texture of textures) {
      this.disposeTexture(texture)
    }
  }

  /**
   * 解析 ZIP 归档文件
   * 实现参考 PlayCanvas 的 parseZipArchive
   */
  private parseZipArchive(data: ArrayBuffer, signal = this.options.signal): ZipFile[] {
    throwIfAborted(signal)
    const dataView = new DataView(data)
    const u16 = (offset: number) => dataView.getUint16(offset, true)
    const u32 = (offset: number) => dataView.getUint32(offset, true)

    // 读取 End of Central Directory Record (EOCD)
    const extractEocd = (offset: number) => {
      return {
        magic: u32(offset),
        numFiles: u16(offset + 8),
        cdSizeBytes: u32(offset + 12),
        cdOffsetBytes: u32(offset + 16),
      }
    }

    // 读取 Central Directory Record (CDR)
    const extractCdr = (offset: number) => {
      const filenameLength = u16(offset + 28)
      const extraFieldLength = u16(offset + 30)
      const fileCommentLength = u16(offset + 32)
      return {
        magic: u32(offset),
        compressionMethod: u16(offset + 10),
        compressedSizeBytes: u32(offset + 20),
        uncompressedSizeBytes: u32(offset + 24),
        lfhOffsetBytes: u32(offset + 42),
        filename: new TextDecoder().decode(new Uint8Array(data, offset + 46, filenameLength)),
        recordSizeBytes: 46 + filenameLength + extraFieldLength + fileCommentLength,
      }
    }

    // 读取 Local File Header (LFH)
    const extractLfh = (offset: number) => {
      const filenameLength = u16(offset + 26)
      const extraLength = u16(offset + 28)
      return {
        magic: u32(offset),
        offsetBytes: offset + 30 + filenameLength + extraLength,
      }
    }

    // 读取 EOCD（位于文件末尾前 22 字节）
    const eocd = extractEocd(dataView.byteLength - 22)

    if (eocd.magic !== 0x06054b50) {
      throw new Error('无效的 ZIP 文件: 未找到 EOCD')
    }

    if (eocd.cdOffsetBytes === 0xffffffff || eocd.cdSizeBytes === 0xffffffff) {
      throw new Error('无效的 ZIP 文件: 不支持 Zip64')
    }

    // 遍历中央目录记录
    const result: ZipFile[] = []
    let offset = eocd.cdOffsetBytes

    for (let i = 0; i < eocd.numFiles; i++) {
      throwIfAborted(signal)
      const cdr = extractCdr(offset)
      if (cdr.magic !== 0x02014b50) {
        throw new Error('无效的 ZIP 文件: 未找到 CDR')
      }

      const lfh = extractLfh(cdr.lfhOffsetBytes)
      if (lfh.magic !== 0x04034b50) {
        throw new Error('无效的 ZIP 文件: 未找到 LFH')
      }

      const compressionMap: Record<number, 'none' | 'deflate' | 'unknown'> = {
        0: 'none',
        8: 'deflate',
      }

      result.push({
        filename: cdr.filename,
        compression: compressionMap[cdr.compressionMethod] ?? 'unknown',
        data: new Uint8Array(data, lfh.offsetBytes, cdr.compressedSizeBytes),
      })

      offset += cdr.recordSizeBytes
    }

    return result
  }

  /**
   * 使用浏览器原生 DecompressionStream API 解压 deflate 数据
   */
  private async inflate(compressed: Uint8Array, signal = this.options.signal): Promise<Uint8Array> {
    throwIfAborted(signal)
    if (typeof DecompressionStream === 'undefined') {
      throw new Error('浏览器不支持 DecompressionStream API')
    }

    const ds = new DecompressionStream('deflate-raw')
    // 使用 slice 确保获得独立的 ArrayBuffer
    const buffer = compressed.buffer.slice(
      compressed.byteOffset,
      compressed.byteOffset + compressed.byteLength
    ) as ArrayBuffer
    const blob = new Blob([buffer])
    const out = blob.stream().pipeThrough(ds)
    const reader = out.getReader()
    const chunks: Uint8Array[] = []
    let totalLength = 0
    let cancelPromise: Promise<void> | null = null
    const abort = () => {
      cancelPromise ??= reader.cancel(createAbortError()).catch(() => undefined)
    }

    signal?.addEventListener('abort', abort, { once: true })
    try {
      while (true) {
        throwIfAborted(signal)
        const { done, value } = await reader.read()
        if (done) {
          break
        }
        chunks.push(value)
        totalLength += value.byteLength
      }
      throwIfAborted(signal)

      const result = new Uint8Array(totalLength)
      let offset = 0
      for (const chunk of chunks) {
        result.set(chunk, offset)
        offset += chunk.byteLength
      }
      return result
    } catch (error) {
      if (signal?.aborted) {
        throw createAbortError()
      }
      throw error
    } finally {
      signal?.removeEventListener('abort', abort)
      await cancelPromise
      reader.releaseLock()
    }
  }

  /**
   * 从内存中的文件加载 SOGS 数据
   */
  private async loadFromMemory(
    meta: Record<string, unknown>,
    files: ZipFile[]
  ): Promise<SplatData | SogsData> {
    // 类型守卫：检查 meta 是否具有预期的属性结构
    const hasMeta = (m: Record<string, unknown>): m is Record<string, { files?: string[] }> => {
      return true // 宽松检查，由运行时验证
    }

    if (!hasMeta(meta)) {
      throw new Error('Invalid meta structure')
    }

    // 提取所需的文件名
    const filenames = [
      ...(meta.means?.files ?? []),
      ...(meta.scales?.files ?? []),
      ...(meta.quats?.files ?? []),
      ...(meta.sh0?.files ?? []),
      ...(meta.shN?.files ?? []),
    ]

    // 创建文件名到数据的映射
    const fileMap = new Map<string, Uint8Array>()
    for (const file of files) {
      fileMap.set(file.filename, file.data)
    }

    // 验证所有引用的文件都存在
    for (const filename of filenames) {
      if (!fileMap.has(filename)) {
        throw new Error(`SOG Bundle 中缺少文件: ${filename}`)
      }
    }

    // 使用 SOGLoader 的内部方法加载数据
    // loadChunkFromMemory 会自动处理 v1/v2 格式升级
    return this.sogLoader.loadChunkFromMemory(meta, fileMap)
  }
}
